import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setLang, setTheme, selectModules, isModuleEnabled } from '../../store';
import { useT } from '../../i18n';
import toast from 'react-hot-toast';

const SESSION_TIMEOUT_KEY = 'bg_session_timeouts';
// Map backend roles to session settings keys
const ROLE_SESSION_MAP = {
  admin: 'admin', super_admin: 'admin', staff: 'admin',
  barrier_op: 'barrier_op', chef: 'chef', restaurant: 'restaurant',
  client: 'customer', customer: 'customer',
};

// Modul key → nav item mapping (module-gated items)
const MODULE_NAV = {
  barrier:       { to: '/barriers',      icon: '⊞', labelKey: 'nav_barriers' },
  restaurant:    { to: '/restaurants',   icon: '◉', labelKey: 'nav_restaurants' },
  wallet:        { to: '/wallet',        icon: '◆', labelKey: 'nav_wallet' },
  announcements: { to: '/announcements', icon: '📢', labelKey: 'nav_announcements' },
  delivery:      { to: '/delivery',      icon: '📦', labelKey: 'nav_delivery' },
  services:      { to: '/services',      icon: '🖥️', labelKey: 'nav_services' },
};

// Nav items that are always visible regardless of module state
const FIXED_NAV_TOP = [
  { to: '/', icon: '◈', labelKey: 'nav_dashboard', end: true },
];
const FIXED_NAV_BOTTOM = [
  { to: '/users',     icon: '◎', labelKey: 'nav_users' },
  { to: '/roles',     icon: '⬡', labelKey: 'nav_roles' },
  { to: '/analytics', icon: '▲', labelKey: 'nav_analytics' },
  { to: '/reports',   icon: '📊', labelKey: 'nav_reports' },
  { to: '/settings',  icon: '⚙', labelKey: 'nav_settings' },
];

const THEMES = ['dark', 'light', 'glass'];
const THEME_ICONS = { dark: '🌙', light: '☀️', glass: '✦' };
const LANGS = ['az', 'en', 'ru'];

const SIDEBAR_FULL = 240;
const SIDEBAR_COLLAPSED = 64;

export default function AdminLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useT();
  const user = useSelector(s => s.auth.user);
  const modules = useSelector(selectModules);
  const lang = useSelector(s => s.i18n?.lang || 'az');
  const themeMode = useSelector(s => s.theme?.mode || 'dark');
  const [collapsed, setCollapsed] = useState(false);

  // ── Inactivity auto-logout ───────────────────────────────────────────────────
  useEffect(() => {
    const timeouts = (() => {
      try { return JSON.parse(localStorage.getItem(SESSION_TIMEOUT_KEY) || '{}'); } catch { return {}; }
    })();
    const roleKey = ROLE_SESSION_MAP[user?.role] || 'admin';
    const timeoutSec = timeouts[roleKey] ?? 0; // 0 = unlimited
    if (!timeoutSec) return;

    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch(logout());
        toast.error('Sessiya vaxtı bitdi — yenidən daxil olun');
        navigate('/login');
      }, timeoutSec * 1000);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user?.role, dispatch, navigate]);

  const sideW = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_FULL;

  const handleLogout = () => {
    dispatch(logout());
    toast.success(t('logout'));
    navigate('/login');
  };

  // Build nav list: fixed top + enabled module items + fixed bottom
  const moduleNavItems = Object.entries(MODULE_NAV)
    .filter(([key]) => isModuleEnabled(modules, key))
    .map(([, item]) => item);

  const topNav  = [...FIXED_NAV_TOP, ...moduleNavItems];
  const bottomNav = [...FIXED_NAV_BOTTOM];

  const navLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      title={collapsed ? t(item.labelKey) : undefined}
      style={({ isActive }) => ({
        ...S.navItem,
        color:      isActive ? 'var(--primary)' : 'var(--text3)',
        background: isActive
          ? 'linear-gradient(90deg, var(--primary)20 0%, var(--primary)10 100%)'
          : 'transparent',
        borderLeft: isActive ? '2px solid var(--primary)' : '2px solid transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        paddingLeft: collapsed ? 0 : 12,
      })}
    >
      <span style={{ ...S.navIcon, fontSize: 18 }}>{item.icon}</span>
      {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t(item.labelKey)}</span>}
    </NavLink>
  );

  return (
    <div style={{ ...S.root, background: 'var(--bg)' }}>
      {/* Sidebar */}
      <aside style={{
        ...S.sidebar,
        width: sideW,
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
      }}>
        {/* Brand */}
        <div style={{
          ...S.brand,
          borderBottom: '1px solid var(--sidebar-border)',
          padding: collapsed ? '0 0 20px' : '0 12px 16px',
          justifyContent: 'center',
        }}>
          {collapsed ? (
            <img src="/assets/logo-mark.png" alt="BG" style={{ width: 36, height: 36 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/assets/logo-mark.png" alt="BG Sync" style={{ width: 38, height: 38, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: '0.06em' }}>BAKU GATE</div>
                <div style={{ ...S.brandSub, color: 'var(--text3)' }}>Admin Panel · <span style={{ color: 'var(--primary)', fontWeight: 700 }}>by.Elvin</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(v => !v)}
          title={collapsed ? 'Genişlət' : 'Daralt'}
          style={{
            position: 'absolute', top: 22, right: -11,
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--card)', border: '1px solid var(--border)',
            color: 'var(--text3)', fontSize: 11, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, boxShadow: '0 2px 6px var(--shadow)',
            lineHeight: 1,
          }}
        >
          {collapsed ? '›' : '‹'}
        </button>

        {/* Nav top */}
        <nav style={{ ...S.nav, padding: collapsed ? '8px 4px' : '8px 8px' }}>
          {topNav.map(navLink)}

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--sidebar-border)', margin: '6px 4px' }} />

          {bottomNav.map(navLink)}
        </nav>

        {/* Bottom controls */}
        <div style={{ ...S.sideBottom, borderTop: '1px solid var(--sidebar-border)' }}>
          {!collapsed ? (
            <>
              {/* Theme toggle */}
              <div style={S.toggleRow}>
                {THEMES.map(tm => (
                  <button key={tm} title={t('theme_' + tm)}
                    style={{
                      ...S.iconBtn,
                      background:  themeMode === tm ? 'var(--primary)' : 'var(--bg3)',
                      color:       themeMode === tm ? '#fff' : 'var(--text3)',
                      fontWeight:  themeMode === tm ? 700 : 400,
                    }}
                    onClick={() => dispatch(setTheme(tm))}>
                    {THEME_ICONS[tm]}
                  </button>
                ))}
              </div>

              {/* Lang toggle */}
              <div style={{ ...S.toggleRow, marginBottom: 12 }}>
                {LANGS.map(l => (
                  <button key={l}
                    style={{
                      ...S.langBtn,
                      background: lang === l ? 'var(--primary)' : 'var(--bg3)',
                      color:      lang === l ? '#fff' : 'var(--text3)',
                    }}
                    onClick={() => dispatch(setLang(l))}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* User info */}
              <div style={S.userInfo}>
                <div style={S.avatar}>{(user?.fullName?.[0] || 'A').toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...S.userName, color: 'var(--text)' }}>{user?.fullName || 'Admin'}</div>
                  <div style={{ ...S.userPhone, color: 'var(--text3)' }}>{user?.phone || ''}</div>
                </div>
              </div>

              {/* Logout */}
              <button
                style={{ ...S.logoutBtn, borderColor: 'var(--border)', color: 'var(--text3)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--danger)22';
                  e.currentTarget.style.color = 'var(--danger)';
                  e.currentTarget.style.borderColor = 'var(--danger)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text3)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
                onClick={handleLogout}>
                {t('logout')}
              </button>
            </>
          ) : (
            /* Collapsed: avatar + logout only */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ ...S.avatar, width: 30, height: 30, fontSize: 13 }}>
                {(user?.fullName?.[0] || 'A').toUpperCase()}
              </div>
              <button
                title={t('logout')}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)',
                  background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontSize: 14,
                }}
                onClick={handleLogout}>⏻</button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main style={{ ...S.main, marginLeft: sideW, background: 'var(--bg)' }}>
        <Outlet />
      </main>
    </div>
  );
}

const S = {
  root: { display: 'flex', minHeight: '100vh' },
  sidebar: {
    display: 'flex', flexDirection: 'column', padding: '24px 0',
    position: 'fixed', height: '100vh', top: 0, left: 0, zIndex: 100,
    transition: 'width 0.2s ease', overflow: 'hidden',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12 },
  brandIcon: { fontSize: 28, flexShrink: 0 },
  brandName: { fontSize: 15, fontWeight: 800, letterSpacing: '0.06em' },
  brandSub: { fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 2 },
  nav: { flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', borderRadius: 8,
    fontSize: 13, fontWeight: 500,
    transition: 'all 0.18s', textDecoration: 'none',
  },
  navIcon: { width: 22, textAlign: 'center', flexShrink: 0 },
  sideBottom: { padding: '14px 12px', flexShrink: 0 },
  toggleRow: { display: 'flex', gap: 4, marginBottom: 8 },
  iconBtn: { flex: 1, padding: '6px 0', borderRadius: 7, border: 'none', fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' },
  langBtn: { flex: 1, padding: '5px 0', borderRadius: 7, border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.05em', transition: 'all 0.2s' },
  userInfo: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, overflow: 'hidden' },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#00c896,#00e5a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: '#0d0d12', flexShrink: 0 },
  userName: { fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userPhone: { fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: { width: '100%', padding: '7px', background: 'transparent', border: '1px solid', borderRadius: 7, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s' },
  logoMark: { width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg, #0a6b47, #00c896)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: '#fff', letterSpacing: '0.04em' },
  main: { flex: 1, padding: '32px', overflowY: 'auto', minHeight: '100vh', transition: 'margin-left 0.2s ease' },
};
